/**
 * Specialty in ChartMind: which note outline and documentation stance the Chart step uses.
 *
 * Ported from ChartMind-7 (HealthDeskMinusOne 7e9c05ed): the six enabled specialties, their
 * outpatient templates (config/defaultSpecialtyTemplates.js), inpatient admission and progress
 * outlines (chartMind7InpatientNoteTemplates.js) and the documentation outline sent with the note
 * request (chartMind7DocumentationOutline.js), word for word.
 *
 * MinusOne seeds a prompt per step, specialty and setting: the base prompt plus a short "SPECIALTY
 * AND SETTING FOCUS" addendum. Here the same addenda (specialtyFocus.json, word for word) are
 * appended to each step's request instead, so no prompt is added. The one addition is a Pending
 * section on the inpatient outlines, which our Daily Plan fills.
 */

import {
  ENCOUNTER_SETTINGS,
  NOTE_FORMATS,
  isInpatient,
  normalizeEncounterSetting,
  normalizeNoteFormat,
  promptForSetting,
} from "./inpatient";
import SPECIALTY_FOCUS from "./specialtyFocus.json";

export const SPECIALTIES = [
  { value: "em", label: "Emergency Medicine" },
  { value: "im", label: "Internal Medicine" },
  { value: "fm", label: "Family Medicine" },
  { value: "peds", label: "Pediatrics" },
  { value: "obgyn", label: "Obstetrics and Gynecology" },
  { value: "surg", label: "Surgery" },
];

/** ChartMind-7's default: Emergency Medicine. Unknown or missing values read as it. */
export const DEFAULT_SPECIALTY = "em";

export const normalizeSpecialty = (value) =>
  SPECIALTIES.some((s) => s.value === value) ? value : DEFAULT_SPECIALTY;

export const specialtyLabel = (value) =>
  SPECIALTIES.find((s) => s.value === normalizeSpecialty(value)).label;

/**
 * MinusOne's focus text for this step, specialty and setting, or undefined. Inpatient notes have
 * none: their instructions are in chartmind-notes-inpatient.
 */
export const specialtyFocus = (basePromptId, specialty, setting) =>
  SPECIALTY_FOCUS[basePromptId]?.[`${normalizeSpecialty(specialty)}--${normalizeEncounterSetting(setting)}`];

/** Prompt options for a step: the setting's prompt (see promptForSetting) plus the focus. */
export const promptOptions = (basePromptId, setting, specialty) => ({
  ...promptForSetting(basePromptId, setting),
  focus: specialtyFocus(basePromptId, specialty, setting),
});

// ============================================================================
// Outpatient templates
// ============================================================================

const OUTPATIENT_SPECIALTY_DEFINITIONS = {
  "Emergency Medicine": {
    "displayName": "Emergency Medicine",
    "description": "Acute care focused documentation with emergency department course and disposition",
    "requiredSections": [
      "Chief Complaint",
      "History of Present Illness",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Physical Examination",
      "Emergency Department Course",
      "Diagnostic Studies",
      "Assessment and Plan",
      "Disposition"
    ],
    "optionalSections": [
      "Vital Signs",
      "Pain Score",
      "Glasgow Coma Scale",
      "Procedures Performed",
      "Consultations",
      "Patient Instructions"
    ],
    "formatRules": {
      "Chief Complaint": "Brief, specific presenting complaint",
      "Disposition": "Discharge, admit, transfer with specific instructions",
      "Emergency Department Course": "Chronological timeline of ED care"
    },
    "terminology": {
      "Disposition": "Final patient placement decision"
    },
    "promptInstructions": "Create emergency medicine documentation focusing on rapid assessment, differential diagnosis, and safe disposition with clear follow-up instructions."
  },
  "Internal Medicine": {
    "displayName": "Internal Medicine",
    "description": "General internal medicine documentation with comprehensive history and physical",
    "requiredSections": [
      "Chief Complaint",
      "History of Present Illness",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Family History",
      "Review of Systems",
      "Physical Examination",
      "Assessment and Plan"
    ],
    "optionalSections": [
      "Vital Signs",
      "Laboratory Results",
      "Imaging Results",
      "Procedures",
      "Patient Education",
      "Follow-up Instructions"
    ],
    "formatRules": {
      "Assessment and Plan": "List problems with specific plans for each",
      "Medications": "Include dose, frequency, indication",
      "Physical Examination": "Systematic head-to-toe examination"
    },
    "terminology": {
      "Chief Complaint": "Brief statement in patient's own words",
      "HPI": "Chronological narrative of present illness",
      "ROS": "System-based symptom review"
    },
    "promptInstructions": "Create comprehensive internal medicine documentation focusing on differential diagnosis and evidence-based management plans."
  },
  "Family Medicine": {
    "displayName": "Family Medicine",
    "description": "Family-centered care with focus on prevention and chronic disease management",
    "requiredSections": [
      "Chief Complaint",
      "History of Present Illness",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Family History",
      "Physical Examination",
      "Assessment and Plan"
    ],
    "optionalSections": [
      "Preventive Care Screening",
      "Health Maintenance",
      "Patient Education",
      "Immunization Status",
      "Behavioral Health Screening"
    ],
    "formatRules": {
      "Social History": "Include occupation, exercise, diet, substance use",
      "Family History": "Focus on hereditary conditions and age of onset",
      "Preventive Care Screening": "Age-appropriate screening recommendations"
    },
    "terminology": {
      "Health Maintenance": "Preventive care measures and screening schedules"
    },
    "promptInstructions": "Focus on comprehensive family medicine care including prevention, health maintenance, and chronic disease management across all ages."
  },
  "Pediatrics": {
    "displayName": "Pediatrics",
    "description": "Age-appropriate pediatric documentation with growth and development",
    "requiredSections": [
      "Chief Complaint",
      "History of Present Illness",
      "Birth History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Immunization Status",
      "Developmental History",
      "Social History",
      "Family History",
      "Physical Examination",
      "Growth Parameters",
      "Assessment and Plan"
    ],
    "optionalSections": [
      "Feeding History",
      "School Performance",
      "Behavioral Assessment",
      "Safety Counseling",
      "Anticipatory Guidance"
    ],
    "formatRules": {
      "Growth Parameters": "Height, weight, head circumference with percentiles",
      "Developmental History": "Age-appropriate milestones",
      "Birth History": "Gestational age, birth weight, complications",
      "Immunization Status": "Current per CDC schedule"
    },
    "terminology": {
      "Growth Parameters": "Height/weight/HC percentiles for age",
      "Developmental Milestones": "Age-appropriate gross/fine motor, language, social skills"
    },
    "promptInstructions": "Create pediatric documentation with focus on growth, development, and age-appropriate care including preventive measures and parent education."
  },
  "Obstetrics and Gynecology": {
    "displayName": "Obstetrics and Gynecology",
    "description": "Women's health documentation with obstetric and gynecologic focus",
    "requiredSections": [
      "Chief Complaint",
      "History of Present Illness",
      "Obstetric History (G/P - TPAL)",
      "Gynecologic History",
      "Past Medical History",
      "Medications",
      "Allergies",
      "Social History",
      "Physical Examination",
      "Pelvic Examination",
      "Assessment and Plan"
    ],
    "optionalSections": [
      "Last Menstrual Period",
      "Contraceptive History",
      "STI Screening",
      "Pap Smear History",
      "Breast Exam",
      "Prenatal Labs"
    ],
    "formatRules": {
      "Obstetric History": "G_P_ format with TPAL breakdown (Term, Preterm, Abortions, Living)",
      "Last Menstrual Period": "Date and cycle regularity",
      "Pelvic Examination": "Systematic examination of external genitalia, vagina, cervix, uterus, adnexa"
    },
    "terminology": {
      "TPAL": "Term births, Preterm births, Abortions (spontaneous/induced), Living children",
      "G/P": "Gravida (total pregnancies) / Para (births after 20 weeks)"
    },
    "promptInstructions": "Create OB/GYN documentation with proper obstetric notation (G/P-TPAL format), detailed gynecologic history, and comprehensive pelvic examination findings."
  },
  "Surgery": {
    "displayName": "Surgery",
    "description": "Surgical documentation with operative planning and risk assessment",
    "requiredSections": [
      "Chief Complaint",
      "History of Present Illness",
      "Past Medical History",
      "Past Surgical History",
      "Medications",
      "Allergies",
      "Social History",
      "Physical Examination",
      "Preoperative Assessment",
      "Assessment and Plan"
    ],
    "optionalSections": [
      "ASA Physical Status",
      "Surgical Risk Assessment",
      "Informed Consent",
      "Preoperative Orders",
      "Postoperative Instructions",
      "Operative Planning"
    ],
    "formatRules": {
      "ASA Physical Status": "ASA I-VI classification with rationale",
      "Past Surgical History": "Procedure, date, complications, surgeon",
      "Surgical Risk Assessment": "Cardiac, pulmonary, bleeding risk",
      "Operative Planning": "Procedure details, approach, anticipated complications"
    },
    "terminology": {
      "ASA": "American Society of Anesthesiologists physical status classification",
      "NPO": "Nothing by mouth status"
    },
    "promptInstructions": "Create surgical documentation with thorough preoperative risk assessment, ASA classification, and detailed operative planning with consideration of perioperative management."
  },
};

/** "Obstetric History (G/P - TPAL)" → "obstetricHistory". */
const toSectionKey = (title) => {
  const words = String(title || "")
    .replace(/\([^)]*\)/g, " ")
    .replace(/[^a-zA-Z0-9]+/g, " ")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (words.length === 0) return "section";
  return words
    .map((w, i) => (i === 0 ? w.toLowerCase() : w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()))
    .join("");
};

/** Required sections first, then optional; format rules and terminology become placeholders. */
const specialtyToTemplate = (key, def) => {
  const usedKeys = new Set();
  const makeSections = (labels, required) =>
    (labels || []).map((label) => {
      let sectionKey = toSectionKey(label);
      while (usedKeys.has(sectionKey)) sectionKey = `${sectionKey}_`;
      usedKeys.add(sectionKey);
      const placeholder = def.formatRules?.[label] || def.terminology?.[label] || undefined;
      return { key: sectionKey, title: label, placeholder, required };
    });

  return {
    id: `specialty_${toSectionKey(key)}`,
    name: def.displayName || key,
    description: def.description || "",
    guidance: def.promptInstructions || undefined,
    sections: [...makeSections(def.requiredSections, true), ...makeSections(def.optionalSections, false)],
  };
};

// ============================================================================
// Inpatient templates
// ============================================================================

const section = (key, title, required, placeholder) => ({
  key,
  title,
  required,
  ...(placeholder ? { placeholder } : {}),
});

const PENDING_SECTION = section("pending", "Pending", false, "Checked items still waiting on a result");

const INPATIENT_ADMISSION_TEMPLATES = {
  em: {
    id: "chartmind7_inpatient_em_admission",
    name: "Emergency Medicine",
    description: "Emergency medicine note for a patient boarding or in observation",
    guidance: "Document reassessment, the emergency department course, studies that changed management, and the disposition or handoff. Do not invent a triage level.",
    sections: [
      section("chiefComplaint", "Chief Complaint", true, "Brief presenting complaint"),
      section("historyOfPresentIllness", "History of Present Illness", true),
      section("pastMedicalHistory", "Past Medical History", true),
      section("medications", "Medications", true, "Name, dose, and frequency when stated"),
      section("allergies", "Allergies", true),
      section("physicalExamination", "Physical Examination", true, "Examination performed, including vital signs when stated"),
      section("emergencyDepartmentCourse", "Emergency Department Course", true, "Reassessment, response to treatment, and serial testing while boarding or in observation"),
      section("diagnosticStudies", "Diagnostic Studies", true),
      section("assessmentAndPlan", "Assessment and Plan", true, "Working diagnosis, treatment during boarding or observation, and the handoff"),
      section("disposition", "Disposition", true, "Admit, continued observation, or the service receiving the handoff"),
      section("pastSurgicalHistory", "Past Surgical History", false),
      section("socialHistory", "Social History", false),
      section("proceduresPerformed", "Procedures Performed", false),
      section("consultations", "Consultations", false),
      PENDING_SECTION,
    ],
  },
  im: {
    id: "chartmind7_inpatient_im_admission",
    name: "Internal Medicine",
    description: "Hospitalist admission history and physical",
    guidance: "Organize the assessment and plan by problem, including medication changes and items still pending.",
    sections: [
      section("chiefComplaint", "Chief Complaint", true, "Brief reason for admission"),
      section("historyOfPresentIllness", "History of Present Illness", true),
      section("pastMedicalHistory", "Past Medical History", true),
      section("pastSurgicalHistory", "Past Surgical History", true),
      section("medications", "Medications", true, "Dose, frequency, and indication when stated"),
      section("allergies", "Allergies", true),
      section("socialHistory", "Social History", true),
      section("familyHistory", "Family History", true),
      section("physicalExamination", "Physical Examination", true),
      section("assessmentAndPlan", "Assessment and Plan", true, "One entry per active problem, with the plan for that problem"),
      section("reviewOfSystems", "Review of Systems", false),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("hospitalCourse", "Hospital Course", false, "Interval events and the trajectory since admission"),
      PENDING_SECTION,
    ],
  },
  fm: {
    id: "chartmind7_inpatient_fm_admission",
    name: "Family Medicine",
    description: "Family medicine inpatient admission history and physical",
    guidance: "Organize the assessment and plan by problem. Keep family context and the return to the usual clinician when the source describes them.",
    sections: [
      section("chiefComplaint", "Chief Complaint", true, "Brief reason for admission"),
      section("historyOfPresentIllness", "History of Present Illness", true),
      section("pastMedicalHistory", "Past Medical History", true),
      section("pastSurgicalHistory", "Past Surgical History", true),
      section("medications", "Medications", true, "Dose, frequency, and indication when stated"),
      section("allergies", "Allergies", true),
      section("socialHistory", "Social History", true, "Living situation and family context"),
      section("familyHistory", "Family History", true),
      section("physicalExamination", "Physical Examination", true),
      section("assessmentAndPlan", "Assessment and Plan", true, "One entry per active problem, with the plan for that problem"),
      section("reviewOfSystems", "Review of Systems", false),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("hospitalCourse", "Hospital Course", false, "Interval events and the trajectory since admission"),
      section("preventiveCareAddressed", "Preventive Care Addressed", false, "Only preventive care addressed during this stay"),
      section("followUpWithPrimaryClinician", "Follow-up with Primary Clinician", false),
      PENDING_SECTION,
    ],
  },
  peds: {
    id: "chartmind7_inpatient_peds_admission",
    name: "Pediatrics",
    description: "Pediatric inpatient admission history and physical",
    guidance: "Include who gave the history, weight, diet and hydration, and a plan a caregiver can follow.",
    sections: [
      section("chiefComplaint", "Chief Complaint", true, "Brief reason for admission"),
      section("historyOfPresentIllness", "History of Present Illness", true, "Include who gave the history"),
      section("birthHistory", "Birth History", true, "Gestational age, birth weight, and complications when relevant to this admission"),
      section("pastMedicalHistory", "Past Medical History", true),
      section("medications", "Medications", true, "Include the stated dose"),
      section("allergies", "Allergies", true),
      section("immunizationStatus", "Immunization Status", true),
      section("developmentalHistory", "Developmental History", true),
      section("dietAndHydration", "Diet and Hydration", true, "Feeding, intake, and hydration"),
      section("socialHistory", "Social History", true, "Caregivers and living situation"),
      section("familyHistory", "Family History", true),
      section("physicalExamination", "Physical Examination", true),
      section("growthParameters", "Growth Parameters", true, "Weight, and length or height when stated"),
      section("assessmentAndPlan", "Assessment and Plan", true, "Plan a caregiver can follow"),
      section("reviewOfSystems", "Review of Systems", false),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("hospitalCourse", "Hospital Course", false),
      PENDING_SECTION,
    ],
  },
  obgyn: {
    id: "chartmind7_inpatient_obgyn_admission",
    name: "Obstetrics and Gynecology",
    description: "Obstetric or gynecologic admission history and physical",
    guidance: "Use gravida and para notation. Record gestational age or menstrual dating. Include fetal status when the patient is pregnant, and the postoperative day when this is a surgical admission.",
    sections: [
      section("chiefComplaint", "Chief Complaint", true),
      section("historyOfPresentIllness", "History of Present Illness", true),
      section("obstetricHistory", "Obstetric History", true, "Gravida and para, with TPAL when stated"),
      section("gestationalAge", "Gestational Age", true, "Gestational age, last menstrual period, or estimated due date"),
      section("gynecologicHistory", "Gynecologic History", true),
      section("pastMedicalHistory", "Past Medical History", true),
      section("pastSurgicalHistory", "Past Surgical History", true),
      section("medications", "Medications", true),
      section("allergies", "Allergies", true),
      section("physicalExamination", "Physical Examination", true, "The examination that was performed"),
      section("assessmentAndPlan", "Assessment and Plan", true, "Maternal plan, fetal plan when pregnant, or postoperative plan when this is a surgical admission"),
      section("fetalStatus", "Fetal Status", false, "Fetal heart rate, movement, and presentation when described"),
      section("prenatalCourse", "Prenatal Course", false),
      section("socialHistory", "Social History", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("postoperativeDay", "Postoperative Day", false, "Procedure and postoperative day for a gynecologic surgical admission"),
      section("pelvicExamination", "Pelvic Examination", false, "Only a pelvic examination that was performed"),
      PENDING_SECTION,
    ],
  },
  surg: {
    id: "chartmind7_inpatient_surg_admission",
    name: "Surgery",
    description: "Surgical inpatient admission history and physical",
    guidance: "Document the indication, the operation, and the postoperative day. Put pain control, diet, activity, prophylaxis, and named complications in the plan.",
    sections: [
      section("chiefComplaint", "Chief Complaint", true),
      section("historyOfPresentIllness", "History of Present Illness", true, "Indication for the operation or the reason for admission"),
      section("pastMedicalHistory", "Past Medical History", true),
      section("pastSurgicalHistory", "Past Surgical History", true),
      section("medications", "Medications", true),
      section("allergies", "Allergies", true),
      section("physicalExamination", "Physical Examination", true, "Include the operative site or abdomen when examined"),
      section("procedureAndPostoperativeDay", "Procedure and Postoperative Day", true, "Operation or planned operation, and postoperative day when the patient is postoperative"),
      section("assessmentAndPlan", "Assessment and Plan", true, "Pain control, diet, activity, prophylaxis, and complications named in the source"),
      section("socialHistory", "Social History", false),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("drainsAndWounds", "Drains and Wounds", false),
      section("preoperativeAssessment", "Preoperative Assessment", false, "For a preoperative admission, not a postoperative day"),
      PENDING_SECTION,
    ],
  },
};

const INPATIENT_PROGRESS_TEMPLATES = {
  em: {
    id: "chartmind7_inpatient_em_progress",
    name: "Emergency Medicine",
    description: "Emergency medicine reassessment while boarding or in observation",
    guidance: "Write a reassessment for the same boarding or observation stay. Lead with the updated assessment and plan, then interval events, a focused exam, and new studies. Do not invent a triage level or rewrite a full admission history.",
    sections: [
      section("assessmentAndPlan", "Assessment and Plan", true, "Updated working diagnosis, treatment, and the handoff or disposition"),
      section("intervalEvents", "Interval Events", true, "Events since the last note while boarding or in observation"),
      section("physicalExamination", "Physical Examination", true, "Focused reassessment, including vital signs when stated"),
      section("diagnosticStudies", "Diagnostic Studies", true, "New or serial studies that changed management"),
      section("emergencyDepartmentCourse", "Emergency Department Course", false, "Brief course update when the source supports it"),
      section("disposition", "Disposition", false, "Admit, continued observation, or the service receiving the handoff"),
      section("consultations", "Consultations", false),
      PENDING_SECTION,
    ],
  },
  im: {
    id: "chartmind7_inpatient_im_progress",
    name: "Internal Medicine",
    description: "Hospitalist progress note",
    guidance: "Write a plan-first progress note. Put the assessment and plan by problem first, then interval events, a focused exam, and new data. Do not repeat the full admission history.",
    sections: [
      section("assessmentAndPlan", "Assessment and Plan", true, "One entry per active problem, with the plan for that problem"),
      section("intervalHistory", "Interval History", true, "Events overnight and since the last note"),
      section("physicalExamination", "Physical Examination", true, "Focused examination"),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false, "New results since the last note"),
      section("imagingResults", "Imaging Results", false, "New imaging since the last note"),
      section("medications", "Medications", false, "Medication changes since the last note"),
      PENDING_SECTION,
    ],
  },
  fm: {
    id: "chartmind7_inpatient_fm_progress",
    name: "Family Medicine",
    description: "Family medicine inpatient progress note",
    guidance: "Write a plan-first progress note. Put the assessment and plan by problem first, then interval events and a focused exam. Keep family context and the usual clinician when they still matter. Do not repeat the full admission history.",
    sections: [
      section("assessmentAndPlan", "Assessment and Plan", true, "One entry per active problem, with the plan for that problem"),
      section("intervalHistory", "Interval History", true, "Events overnight and since the last note"),
      section("physicalExamination", "Physical Examination", true, "Focused examination"),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("medications", "Medications", false, "Medication changes since the last note"),
      section("followUpWithPrimaryClinician", "Follow-up with Primary Clinician", false),
      PENDING_SECTION,
    ],
  },
  peds: {
    id: "chartmind7_inpatient_peds_progress",
    name: "Pediatrics",
    description: "Pediatric inpatient progress note",
    guidance: "Write a plan-first progress note. Put the plan a caregiver can follow first, then interval events, weight and intake, and a focused exam. Leave birth history, immunizations, and development on the admission note.",
    sections: [
      section("assessmentAndPlan", "Assessment and Plan", true, "Plan a caregiver can follow"),
      section("intervalHistory", "Interval History", true, "Events overnight and since the last note, including who gave the update"),
      section("dietAndHydration", "Diet and Hydration", true, "Feeding, intake, and hydration since the last note"),
      section("growthParameters", "Growth Parameters", true, "Weight, and length or height when stated"),
      section("physicalExamination", "Physical Examination", true, "Focused examination"),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("medications", "Medications", false, "Medication changes since the last note"),
      PENDING_SECTION,
    ],
  },
  obgyn: {
    id: "chartmind7_inpatient_obgyn_progress",
    name: "Obstetrics and Gynecology",
    description: "Obstetric, postpartum, or gynecologic postoperative progress note",
    guidance: "Write a labor, postpartum, or postoperative-day note. Keep gestational dating and fetal status when the patient is still pregnant. Do not rewrite the full admission history.",
    sections: [
      section("assessmentAndPlan", "Assessment and Plan", true, "Maternal plan, fetal plan when pregnant, or postoperative plan"),
      section("intervalHistory", "Interval History", true, "Labor course, postpartum events, or overnight events since the last note"),
      section("gestationalAge", "Gestational Age", false, "Gestational age when the patient is still pregnant"),
      section("fetalStatus", "Fetal Status", false, "Fetal heart rate, movement, and presentation when the patient is still pregnant"),
      section("postoperativeDay", "Postoperative Day", false, "Procedure and postoperative day when this is a surgical stay"),
      section("physicalExamination", "Physical Examination", true, "Focused examination"),
      section("laboratoryResults", "Laboratory Results", false),
      section("medications", "Medications", false),
      PENDING_SECTION,
    ],
  },
  surg: {
    id: "chartmind7_inpatient_surg_progress",
    name: "Surgery",
    description: "Surgical postoperative progress note",
    guidance: "Lead with the postoperative day, then the plan for pain, diet, activity, and prophylaxis. Include the wound and drains when examined. Do not rewrite the full admission history.",
    sections: [
      section("procedureAndPostoperativeDay", "Procedure and Postoperative Day", true, "Operation and postoperative day"),
      section("assessmentAndPlan", "Assessment and Plan", true, "Pain control, diet, activity, prophylaxis, and complications named in the source"),
      section("intervalHistory", "Interval History", true, "Overnight events and symptoms since the last note"),
      section("physicalExamination", "Physical Examination", true, "Include the operative site or abdomen when examined"),
      section("drainsAndWounds", "Drains and Wounds", false),
      section("vitalSigns", "Vital Signs", false),
      section("laboratoryResults", "Laboratory Results", false),
      section("imagingResults", "Imaging Results", false),
      section("medications", "Medications", false),
      PENDING_SECTION,
    ],
  },
};

/** The note outline for this specialty, setting and (inpatient) note type. */
/**
 * The Events note is the same for every specialty: a short SOAP note on the events the physician
 * entered, written by the inpatient note prompt from this outline.
 */
const EVENTS_NOTE_TEMPLATE = {
  id: "chartmind_inpatient_events",
  name: "Events note",
  description: "Short note on events during an inpatient stay",
  guidance:
    "Write a short SOAP note on the EVENTS the physician entered, not an admission or progress note. Subjective names the event or events. Objective gives each event with the physician's comment. Assessment is the selected diagnoses. Plan lists every PLAN item. A time in parentheses on an event line is the time the physician entered for it: give it with that event. Use no other time unless a comment gives one.",
  sections: [
    section("subjective", "Subjective", true, "The event or events"),
    section("objective", "Objective", true, "Each event with its comment"),
    section("assessment", "Assessment", true, "The selected diagnoses"),
    section("plan", "Plan", true, "Every PLAN item"),
  ],
};

export const noteTemplateFor = (specialty, setting, noteFormat) => {
  const key = normalizeSpecialty(specialty);
  if (isInpatient(setting)) {
    if (normalizeNoteFormat(noteFormat) === NOTE_FORMATS.EVENTS) return EVENTS_NOTE_TEMPLATE;
    const catalog =
      normalizeNoteFormat(noteFormat) === NOTE_FORMATS.PROGRESS
        ? INPATIENT_PROGRESS_TEMPLATES
        : INPATIENT_ADMISSION_TEMPLATES;
    return catalog[key];
  }
  const label = specialtyLabel(key);
  return specialtyToTemplate(label, OUTPATIENT_SPECIALTY_DEFINITIONS[label]);
};

// ============================================================================
// Documentation outline (sent with the note request)
// ============================================================================

/**
 * How each specialty writes in each setting. The specialty template still owns the sections.
 */
const DOCUMENTATION_STANCES = {
  "Emergency Medicine": {
    outpatient: "Write this as an emergency physician's visit note. Document the emergency department course and the disposition. Do not invent a triage level.",
    admission: "Write this as an emergency physician's boarding or observation note. Document reassessment, the emergency department course, serial studies, and the disposition or handoff. Do not invent a triage level.",
    progress: "Write this as an emergency physician's reassessment while the patient is still boarding or in observation. Lead with the updated assessment and plan, then interval events, a focused exam, and new studies. Do not invent a triage level or rewrite a full admission history.",
  },
  "Emergency Department Note": {
    outpatient: "Write this as an emergency physician's visit note. Document the emergency department course and the disposition. Do not invent a triage level.",
    admission: "Write this as an emergency physician's boarding or observation note. Document reassessment, the emergency department course, serial studies, and the disposition or handoff. Do not invent a triage level.",
    progress: "Write this as an emergency physician's reassessment while the patient is still boarding or in observation. Lead with the updated assessment and plan, then interval events, a focused exam, and new studies. Do not invent a triage level or rewrite a full admission history.",
  },
  "Internal Medicine": {
    outpatient: "Write this as an internist's clinic note. Organize the assessment and plan by problem, and include active conditions and medications when the source material supports them.",
    admission: "Write this as a hospitalist's admission history and physical. Organize the assessment and plan by problem. When the source material supports them, include medications and what is still pending.",
    progress: "Write this as a hospitalist's progress note. Put the assessment and plan by problem first, then the interval history, focused exam, and new data. Do not repeat the full admission history. When an Admission H&P is included in the request, use it as prior context for the next hospital day.",
  },
  "Family Medicine": {
    outpatient: "Write this as a family physician's clinic note. Organize the assessment and plan by problem, and include prevention and family context when the source material supports them.",
    admission: "Write this as a family physician's inpatient admission history and physical. Organize the assessment and plan by problem. When the source material supports them, keep the family and outpatient context, prevention that was addressed, and the return to the patient's usual clinician.",
    progress: "Write this as a family physician's inpatient progress note. Put the assessment and plan by problem first, then the interval history and focused exam. Keep family context when it still matters. Do not repeat the full admission history. When an Admission H&P is included in the request, use it as prior context for the next hospital day.",
  },
  "Pediatrics": {
    outpatient: "Write this as a pediatrician's clinic note. When the source material supports them, include who gave the history, growth and development, and a plan a caregiver can follow.",
    admission: "Write this as a pediatrician's inpatient admission history and physical. When the source material supports them, include who gave the history, age and weight, birth history, development, and a plan a caregiver can follow.",
    progress: "Write this as a pediatrician's inpatient progress note. Put the plan a caregiver can follow first, then interval events, weight and intake, and a focused exam. Leave birth history, immunizations, and development on the admission note. When an Admission H&P is included in the request, use it as prior context for the next hospital day.",
  },
  "Obstetrics and Gynecology": {
    outpatient: "Write this as an obstetrician-gynecologist's clinic note. When the source material supports them, use gravida and para notation, menstrual dating, gynecologic history, and the examination that was described.",
    admission: "Write this as an obstetrician-gynecologist's inpatient admission history and physical. When the source material supports them, use gravida and para notation, menstrual or gestational dating, gynecologic history, and the examination that was described, including the pregnancy or postoperative course.",
    progress: "Write this as an obstetrician-gynecologist's labor, postpartum, or postoperative progress note. Keep gestational dating and fetal status when the patient is still pregnant. Do not rewrite the full admission history. When an Admission H&P is included in the request, use it as prior context for the next hospital day.",
  },
  "Surgery": {
    outpatient: "Write this as a surgeon's clinic note. When the source material supports them, include the indication, past operations, and the perioperative plan.",
    admission: "Write this as a surgeon's inpatient admission history and physical. When the source material supports them, include the indication, past operations, the preoperative assessment or postoperative day, and the plan for pain, diet, activity, and complications.",
    progress: "Write this as a surgeon's postoperative progress note. Lead with the postoperative day, then the plan for pain, diet, activity, and prophylaxis, and the wound or drains when examined. Do not rewrite the full admission history. When an Admission H&P is included in the request, use it as prior context for the next hospital day.",
  },
};

const NOTE_FORMAT_LABELS = {
  [NOTE_FORMATS.ADMISSION]: "Admission H&P",
  [NOTE_FORMATS.PROGRESS]: "Progress note",
  [NOTE_FORMATS.EVENTS]: "Events note",
};

/**
 * The outline that tells the model which sections to fill and how this specialty writes them.
 *
 * @param {Object} template - from noteTemplateFor
 * @param {{ setting?: string, noteFormat?: string }} options
 */
export const formatDocumentationOutline = (template, { setting, noteFormat } = {}) => {
  const name = String(template?.name || "Clinical note").trim() || "Clinical note";
  const inpatient = isInpatient(setting);
  const format = inpatient ? normalizeNoteFormat(noteFormat) : null;
  const stance = DOCUMENTATION_STANCES[name]?.[format || ENCOUNTER_SETTINGS.OUTPATIENT] || "";
  const guidance = String(template?.guidance || "").trim();
  const sections = Array.isArray(template?.sections) ? template.sections : [];
  const progress = format === NOTE_FORMATS.PROGRESS;
  const settingLabel = inpatient ? "Inpatient" : "Outpatient";
  const lines = [
    format
      ? `DOCUMENTATION OUTLINE: ${name} (${settingLabel} · ${NOTE_FORMAT_LABELS[format]})`
      : `DOCUMENTATION OUTLINE: ${name} (${settingLabel})`,
    stance,
    guidance && guidance !== stance ? guidance : "",
    "This outline replaces any other note schema, including an emergency-medicine chart schema. Return ONLY a JSON object. Its keys are the section keys below. You may also return pending and lastMenstrualPeriod when this request requires them.",
    "Do not return hpi, ros, pastHistory, physicalExam, mdm, diagnosisImpression, treatmentPlan, dischargeInstructions, or attestation unless that exact key is listed below.",
    format === NOTE_FORMATS.EVENTS
      ? "Keep it short. Do not write a history, examination, or hospital course the events do not contain."
      : progress
        ? "This is a progress note for the next hospital day. Prefer the section order listed below. Do not place the rest of the note inside Assessment and Plan alone when the outline names interval history, examination, or study sections."
        : "Chief Complaint is a brief presenting statement. Put the history in the history section, the examination in the examination section, studies in the study section, and the reasoning and orders in the assessment and plan or in the specialty sections named below. Do not place the rest of the note inside Chief Complaint.",
    "Fill every required section the source material supports. Use an empty string when the source material does not contain that section. Still follow any instruction in this request that requires Last Menstrual Period or a Pending list.",
  ];
  sections.forEach((s) => {
    const flag = s.required ? "required" : "optional";
    const hint = s.placeholder ? ` — ${s.placeholder}` : "";
    lines.push(`- ${s.key} (${s.title}, ${flag})${hint}`);
  });
  if (sections.length) {
    const schema = sections.map((s) => `  "${s.key}": ""`).join(",\n");
    lines.push("JSON shape:", "{", schema, "}");
  }
  return lines.filter(Boolean).join("\n");
};
